import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import RotaRoleResources from "../resources/RotaRoleResources";
import { connect } from "react-redux";
import { logout } from "../redux/actions/LoggedInAction";
import { ACCESS_DENIED } from '../Messages';
import toast from 'react-hot-toast';

function ManageRotaRoles({ isLoggedIn, loggedInUser }) {
    const [roles, setRoles] = useState([]);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [newRoleName, setNewRoleName] = useState("");
    const [editingRole, setEditingRole] = useState(null);
    const [editRoleName, setEditRoleName] = useState("");
    const [loading, setLoading] = useState(false);

    const rotaRoleResources = new RotaRoleResources();

    // Load all rota roles
    const loadRoles = () => {
        rotaRoleResources.getRoles().then(response => {
            if (response && response.body) {
                setRoles(response.body);
            }
        }).catch((err) => {
            toast.error("Failed to load roles: " + err);
        });
    };

    useEffect(() => {
        if (isLoggedIn && loggedInUser['role.name'] === 'admin') {
            loadRoles();
        }
    }, [isLoggedIn, loggedInUser]);

    // Handle create role
    const handleCreateRole = () => {
        if (!newRoleName || newRoleName.trim() === "") {
            toast.error("Role name is required");
            return;
        }

        setLoading(true);
        rotaRoleResources.createRole(newRoleName.trim(), false)
            .then(response => {
                if (response.error && response.error.length > 0) {
                    toast.error("Failed to create role: " + response.error);
                } else {
                    toast.success("Role created successfully");
                    setOpenCreateDialog(false);
                    setNewRoleName("");
                    loadRoles();
                }
            })
            .catch((err) => {
                toast.error("Network error: " + err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Handle edit role
    const handleEditRole = () => {
        if (!editRoleName || editRoleName.trim() === "") {
            toast.error("Role name is required");
            return;
        }

        setLoading(true);
        rotaRoleResources.updateRole(editingRole.id, editRoleName.trim())
            .then(response => {
                if (response.error && response.error.length > 0) {
                    toast.error("Failed to update role: " + response.error);
                } else {
                    toast.success("Role updated successfully");
                    setOpenEditDialog(false);
                    setEditingRole(null);
                    setEditRoleName("");
                    loadRoles();
                }
            })
            .catch((err) => {
                toast.error("Network error: " + err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Handle delete role
    const handleDeleteRole = (role) => {
        if (role.is_core_role) {
            toast.error("Core roles cannot be deleted");
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`);
        if (!confirmed) return;

        setLoading(true);
        rotaRoleResources.deleteRole(role.id)
            .then(response => {
                if (response.error && response.error.length > 0) {
                    toast.error("Failed to delete role: " + response.error);
                } else {
                    toast.success("Role deleted successfully");
                    loadRoles();
                }
            })
            .catch((err) => {
                toast.error("Network error: " + err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Open edit dialog
    const openEditRoleDialog = (role) => {
        if (role.is_core_role) {
            toast.error("Core roles cannot be edited");
            return;
        }
        setEditingRole(role);
        setEditRoleName(role.name);
        setOpenEditDialog(true);
    };

    return (
        <Fragment>
            <Topbar />

            <div className="hero-area">
                <div className="page-banner parallax" style={{ backgroundColor: "#003165" }}>
                    <div className="container">
                        <div className="page-banner-text">
                            {(isLoggedIn && loggedInUser['role.name'] === 'admin') ?
                                <h1 style={{ color: "#ffa800" }} className="block-title"><strong>Manage Rota Roles</strong></h1>
                                :
                                <h1 style={{ color: "#ffa800" }} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div id="main-container">
                {(isLoggedIn && loggedInUser['role.name'] === 'admin') ?
                    <div className="content">
                        <div className="container">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ color: "#ffa800" }}><strong>Rota Roles</strong></h3>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpenCreateDialog(true)}
                                    style={{ backgroundColor: "#ffa800", color: "#003165" }}
                                >
                                    Add New Role
                                </Button>
                            </div>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Role Name</strong></TableCell>
                                            <TableCell><strong>Type</strong></TableCell>
                                            <TableCell align="right"><strong>Actions</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {roles.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    No roles found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            roles.map((role) => (
                                                <TableRow key={role.id}>
                                                    <TableCell>{role.name}</TableCell>
                                                    <TableCell>
                                                        {role.is_core_role ? (
                                                            <span style={{
                                                                backgroundColor: '#4caf50',
                                                                color: 'white',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '12px'
                                                            }}>
                                                                Core Role
                                                            </span>
                                                        ) : (
                                                            <span style={{
                                                                backgroundColor: '#2196f3',
                                                                color: 'white',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '12px'
                                                            }}>
                                                                Custom Role
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {role.is_core_role ? (
                                                            <Tooltip title="Core roles cannot be edited or deleted">
                                                                <span>
                                                                    <IconButton disabled size="small">
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                    <IconButton disabled size="small">
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <>
                                                                <Tooltip title="Edit role">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => openEditRoleDialog(role)}
                                                                        color="primary"
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Delete role">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteRole(role)}
                                                                        color="error"
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                    : <></>
                }
            </div>

            {/* Create Role Dialog */}
            <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Custom Role</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Role Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        style={{ marginTop: '10px' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateRole}
                        variant="contained"
                        disabled={loading}
                        style={{ backgroundColor: "#ffa800", color: "#003165" }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Role Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Rota Role</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Role Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editRoleName}
                        onChange={(e) => setEditRoleName(e.target.value)}
                        style={{ marginTop: '10px' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditRole}
                        variant="contained"
                        disabled={loading}
                        style={{ backgroundColor: "#ffa800", color: "#003165" }}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}

const mapStateToProps = (state) => {
    return {
        isLoggedIn: state.authReducer.isLoggedIn,
        loggedInUser: state.authReducer.user
    };
};

const mapDispatchToProps = {
    logout
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageRotaRoles);
